import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styles from './GuildBattleLineup.module.css';
import { formatDateTime, formatDate as fmtDateOnly } from '../../utils/dateFormat';

const STRATEGIES = [
  'Normal', 'Normal: Light Attacks', 'Normal: Heavy Attacks',
  'Offensive', 'Offensive: Light Attacks', 'Offensive: Heavy Attacks',
  'Defensive', 'Defensive: Light Attacks', 'Defensive: Heavy Attacks',
  'Berserk', 'Berserk: Light Attacks', 'Berserk: Heavy Attacks',
] as const;
type Strategy = typeof STRATEGIES[number];

interface MercConfig {
  strategy: Strategy;
  surrenderPercent: number;
}

interface Mercenary {
  id: number;
  name: string;
  level: number;
  userId: number;
  isHospitalized: boolean;
  guildLeagueConfig: Record<string, MercConfig & { configuredAt?: string }>;
  userName?: string;
}

interface GuildBattle {
  id: number;
  guild1Id: number;
  guild2Id: number;
  minLevel: number;
  maxLevel: number;
  teamSize: number;
  guild1MercenaryIds: number[] | null;
  guild2MercenaryIds: number[] | null;
  scheduledDate: string;
  round: number;
  matchupId: number | null;
  gameNumber: number;
  status: string;
  Guild1: { id: number; name: string; tag: string };
  Guild2: { id: number; name: string; tag: string };
  GuildLeague: { id: number; name: string } | null;
}

// Derive the config key used by the backend for this battle
function configKey(battle: GuildBattle): string {
  return battle.matchupId ? `matchup_${battle.matchupId}` : `battle_${battle.id}`;
}

function getMercConfig(merc: Mercenary, battle: GuildBattle): MercConfig {
  const saved = merc.guildLeagueConfig?.[configKey(battle)];
  return {
    strategy: (saved?.strategy as Strategy) ?? 'Normal',
    surrenderPercent: saved?.surrenderPercent ?? 50,
  };
}

const GuildBattleLineup: React.FC<{
  guildId: number;
  userId: number;
  isLeader: boolean;
  isOfficer: boolean;
}> = ({ guildId, userId, isLeader, isOfficer }) => {
  const [upcomingBattles, setUpcomingBattles] = useState<GuildBattle[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [guildMercenaries, setGuildMercenaries] = useState<Mercenary[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<Array<number | null>>([null, null, null, null]);
  // Per-slot strategy editing state (only for own mercs)
  const [slotConfigs, setSlotConfigs] = useState<Array<MercConfig | null>>([null, null, null, null]);
  const [savingSlot, setSavingSlot] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  const canManage = isLeader || isOfficer;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [battlesRes, mercsRes] = await Promise.all([
        axios.get('/api/guild-league/my-guild/upcoming-battles?limit=5', { withCredentials: true }),
        axios.get(`/api/guild/${guildId}/mercenaries`, { withCredentials: true }),
      ]);
      setUpcomingBattles(battlesRes.data);
      setGuildMercenaries(mercsRes.data);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error fetching battle data:', error);
      }
      setUpcomingBattles([]);
    } finally {
      setLoading(false);
    }
  }, [guildId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync slots & strategy configs when battle or mercs change
  useEffect(() => {
    const battle = upcomingBattles[selectedIndex];
    if (!battle) return;
    const isGuild1 = battle.guild1Id === guildId;
    const savedIds: number[] = isGuild1
      ? (battle.guild1MercenaryIds || [])
      : (battle.guild2MercenaryIds || []);
    const slots: Array<number | null> = [null, null, null, null];
    savedIds.slice(0, 4).forEach((id, i) => { slots[i] = id; });
    setSelectedSlots(slots);

    // Initialise strategy state from saved merc configs
    const configs: Array<MercConfig | null> = slots.map(id => {
      if (id === null) return null;
      const merc = guildMercenaries.find(m => m.id === id);
      return merc ? getMercConfig(merc, battle) : null;
    });
    setSlotConfigs(configs);
  }, [selectedIndex, upcomingBattles, guildId, guildMercenaries]);

  const handleSlotChange = (slotIndex: number, mercId: string) => {
    if (!canManage) return;
    const newId = mercId === '' ? null : parseInt(mercId);
    const newSlots = [...selectedSlots];
    newSlots[slotIndex] = newId;
    setSelectedSlots(newSlots);

    // Update strategy state for the newly chosen merc
    const battle = upcomingBattles[selectedIndex];
    const newConfigs = [...slotConfigs];
    if (newId === null || !battle) {
      newConfigs[slotIndex] = null;
    } else {
      const merc = guildMercenaries.find(m => m.id === newId);
      newConfigs[slotIndex] = merc ? getMercConfig(merc, battle) : { strategy: 'Normal' as Strategy, surrenderPercent: 50 };
    }
    setSlotConfigs(newConfigs);
  };

  const saveStrategy = async (slotIndex: number, config: MercConfig) => {
    const battle = upcomingBattles[selectedIndex];
    const mercId = selectedSlots[slotIndex];
    if (!battle || !mercId) return;

    try {
      setSavingSlot(prev => new Set(prev).add(slotIndex));
      await axios.put(
        `/api/guild-league/battles/${battle.id}/configure-mercenary`,
        { mercenaryId: mercId, strategy: config.strategy, surrenderPercent: config.surrenderPercent },
        { withCredentials: true }
      );
      await fetchData();
    } catch (error: any) {
      setMessage({ text: error.response?.data?.message || 'Failed to save strategy', type: 'error' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSavingSlot(prev => { const next = new Set(prev); next.delete(slotIndex); return next; });
    }
  };

  const handleStrategyChange = (slotIndex: number, value: string) => {
    const newConfigs = [...slotConfigs];
    const current = newConfigs[slotIndex] ?? { strategy: 'Normal' as Strategy, surrenderPercent: 50 };
    const updated = { ...current, strategy: value as Strategy };
    newConfigs[slotIndex] = updated;
    setSlotConfigs(newConfigs);
    saveStrategy(slotIndex, updated);
  };

  const handleSurrenderChange = (slotIndex: number, value: number) => {
    const newConfigs = [...slotConfigs];
    const current = newConfigs[slotIndex] ?? { strategy: 'Normal' as Strategy, surrenderPercent: 50 };
    const updated = { ...current, surrenderPercent: value };
    newConfigs[slotIndex] = updated;
    setSlotConfigs(newConfigs);
    saveStrategy(slotIndex, updated);
  };

  const saveLineup = async () => {
    const battle = upcomingBattles[selectedIndex];
    if (!battle || !canManage) return;
    const selectedIds = selectedSlots.filter((id): id is number => id !== null);
    try {
      setSaving(true);
      await axios.put(
        `/api/guild-league/battles/${battle.id}/select-mercenaries`,
        { mercenaryIds: selectedIds },
        { withCredentials: true }
      );
      setMessage({ text: 'Lineup saved!', type: 'success' });
      await fetchData();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ text: error.response?.data?.message || 'Failed to save lineup', type: 'error' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) => formatDateTime(d);

  const formatShortDate = (d: string) => fmtDateOnly(d);

  if (loading) return <div className={styles.loading}>Loading lineup data...</div>;

  if (upcomingBattles.length === 0) {
    return (
      <div className={styles.noBattle}>
        <p>No upcoming battles scheduled.</p>
        <p className={styles.hint}>Check back when the guild league season is active.</p>
      </div>
    );
  }

  const battle = upcomingBattles[selectedIndex];
  const isGuild1 = battle.guild1Id === guildId;
  const opponent = isGuild1 ? battle.Guild2 : battle.Guild1;
  const myGuild = isGuild1 ? battle.Guild1 : battle.Guild2;

  const eligibleMercs = guildMercenaries.filter(
    m => m.level >= battle.minLevel && m.level <= battle.maxLevel && !m.isHospitalized
  );

  const isAlreadySelected = (mercId: number, currentSlot: number) =>
    selectedSlots.some((id, idx) => idx !== currentSlot && id === mercId);

  const selectedCount = selectedSlots.filter(id => id !== null).length;

  return (
    <div className={styles.container}>
      {/* ── Upcoming battles mini-schedule ── */}
      <div className={styles.scheduleRow}>
        {upcomingBattles.map((b, i) => {
          const isG1 = b.guild1Id === guildId;
          const opp = isG1 ? b.Guild2 : b.Guild1;
          return (
            <button
              key={b.id}
              className={`${styles.scheduleCard} ${i === selectedIndex ? styles.scheduleCardActive : ''}`}
              onClick={() => setSelectedIndex(i)}
            >
              <span className={styles.scheduleCardDate}>{formatShortDate(b.scheduledDate)}</span>
              <span className={styles.scheduleCardOpp}>[{opp.tag}] {opp.name}</span>
              <span className={styles.scheduleCardCap}>Lv ≤{b.maxLevel}</span>
            </button>
          );
        })}
      </div>

      {/* ── Selected battle detail ── */}
      <div className={styles.battleInfo}>
        <div className={styles.battleMeta}>
          <span className={styles.roundBadge}>Round {battle.round}</span>
          <span className={styles.dateBadge}>{formatDate(battle.scheduledDate)}</span>
          <span className={styles.levelBadge}>Lv {battle.minLevel}–{battle.maxLevel}</span>
        </div>
        <div className={styles.matchup}>
          <span className={styles.myGuildName}>{myGuild.name}</span>
          <span className={styles.vs}>VS</span>
          <span className={styles.opponentName}>[{opponent.tag}]&nbsp;{opponent.name}</span>
        </div>
      </div>

      {/* ── Messages ── */}
      {message && <div className={`${styles.message} ${styles[message.type]}`}>{message.text}</div>}
      {!canManage && (
        <div className={`${styles.message} ${styles.info}`}>
          Only guild leaders and officers can manage the battle lineup.
        </div>
      )}

      {/* ── Lineup slots ── */}
      <div className={styles.lineupSection}>
        <div className={styles.lineupHeader}>
          <span className={styles.lineupTitle}>Battle Lineup</span>
          <span className={styles.slotCount}>{selectedCount} / {battle.teamSize} slots filled</span>
        </div>

        <div className={styles.slots}>
          {selectedSlots.map((selectedId, slotIndex) => {
            const merc = selectedId !== null ? guildMercenaries.find(m => m.id === selectedId) : null;
            const isOwnMerc = merc?.userId === userId;
            const cfg = slotConfigs[slotIndex];

            return (
              <div key={slotIndex} className={styles.slotGroup}>
                {/* Merc picker row */}
                <div className={styles.slot}>
                  <span className={styles.slotLabel}>#{slotIndex + 1}</span>
                  <select
                    className={styles.slotSelect}
                    value={selectedId ?? ''}
                    onChange={e => handleSlotChange(slotIndex, e.target.value)}
                    disabled={!canManage}
                  >
                    <option value="">— Empty —</option>
                    {eligibleMercs.map(m => {
                      const taken = isAlreadySelected(m.id, slotIndex);
                      return (
                        <option key={m.id} value={m.id} disabled={taken}>
                          {m.name} (Lv {m.level}), {m.userName}{taken ? ' ✓' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Strategy row (only when a merc is selected) */}
                {merc && cfg && (
                  <div className={`${styles.strategyRow} ${!isOwnMerc ? styles.strategyRowReadOnly : ''}`}>
                    <select
                      className={styles.strategySelect}
                      value={cfg.strategy}
                      disabled={!isOwnMerc || savingSlot.has(slotIndex)}
                      onChange={e => isOwnMerc && handleStrategyChange(slotIndex, e.target.value)}
                    >
                      {STRATEGIES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <div className={styles.surrenderGroup}>
                      <select
                        className={styles.surrenderSelect}
                        value={cfg.surrenderPercent}
                        disabled={!isOwnMerc || savingSlot.has(slotIndex)}
                        onChange={e => isOwnMerc && handleSurrenderChange(slotIndex, parseInt(e.target.value))}
                      >
                        {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90].map(v => (
                          <option key={v} value={v}>{v}% HP</option>
                        ))}
                      </select>
                    </div>
                    {savingSlot.has(slotIndex) && <span className={styles.savingIndicator}>saving…</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {eligibleMercs.length === 0 && (
          <p className={styles.noEligible}>
            No eligible mercenaries for Lv {battle.minLevel}–{battle.maxLevel} (excluding hospitalized).
          </p>
        )}
      </div>

      {canManage && (
        <div className={styles.actions}>
          <button
            className={styles.saveButton}
            onClick={saveLineup}
            disabled={saving || selectedCount === 0}
          >
            {saving ? 'Saving...' : 'Save Lineup'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GuildBattleLineup;
